import React, { useState } from 'react';
import {
  Badge, Button, DataTable, IdCell, Input, KV, Modal, PageHead,
  SearchBox, Tabs, Textarea, dateStr, inr, num,
} from '../components/ui';
import useList from '../hooks/useList';
import { companyApi } from '../api';
import { useToast } from '../context/ToastContext';

const TABS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending KYC' },
  { value: 'approved', label: 'Verified' },
  { value: 'rejected', label: 'Rejected' },
];

const TYPES = ['buyer', 'seller', 'manufacturer', 'distributor', 'supplier', 'logistics', 'insurance', 'finance'];

const Companies = () => {
  const toast = useToast();
  const list = useList(companyApi.list, { verificationStatus: '', type: '', search: '' });

  const [detail, setDetail] = useState(null);
  const [decision, setDecision] = useState(null); // { company, status }
  const [reason, setReason] = useState('');
  const [appeal, setAppeal] = useState(null);
  const [adminNote, setAdminNote] = useState('');
  const [credit, setCredit] = useState(null);
  const [creditValue, setCreditValue] = useState('');
  const [busy, setBusy] = useState(false);

  const openDetail = async company => {
    try {
      const res = await companyApi.get(company._id);
      setDetail(res.data);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const submitDecision = async () => {
    if (decision.status === 'rejected' && !reason.trim()) {
      return toast.error('Give a reason so the applicant knows what to fix');
    }

    setBusy(true);
    try {
      await companyApi.verify(decision.company._id, {
        status: decision.status,
        reason,
        gstVerified: decision.status === 'approved',
        kycVerified: decision.status === 'approved',
      });
      toast.success(
        decision.status === 'approved'
          ? `${decision.company.name} is verified and can now trade`
          : 'Company rejected — the owner has been notified',
      );
      setDecision(null);
      setReason('');
      setDetail(null);
      list.reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  const decideAppeal = async decision => {
    setBusy(true);
    try {
      await companyApi.reviewChangeRequest(appeal._id, { decision, adminNote });
      toast.success(
        decision === 'approved'
          ? 'Approved — they get one edit before the details lock again'
          : 'Change request declined',
      );
      setAppeal(null);
      setAdminNote('');
      setDetail(null);
      list.reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const saveCredit = async () => {
    setBusy(true);
    try {
      await companyApi.setCredit(credit._id, Number(creditValue));
      toast.success('Credit limit updated');
      setCredit(null);
      list.reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'Company',
      render: row => <IdCell image={row.logo} name={row.name} sub={row.code} />,
    },
    { key: 'type', label: 'Type', render: row => <Badge status={row.type} tone="brand" dot={false} /> },
    {
      key: 'gstNumber',
      label: 'GSTIN',
      render: row => (row.gstNumber ? <span className="mono text-sm">{row.gstNumber}</span> : <span className="text-muted">—</span>),
    },
    {
      key: 'location',
      label: 'Location',
      render: row => {
        const a = (row.addresses || [])[0];
        return a ? (
          <>
            <div>{a.city || '—'}</div>
            <div className="cell-muted">{a.state}</div>
          </>
        ) : '—';
      },
    },
    { key: 'totalBusiness', label: 'Business', align: 'right', render: row => inr(row.totalBusiness, true) },
    {
    key: 'verificationStatus',
    label: 'KYC',
    render: row => (
      <div className="row" style={{ gap: 5 }}>
        <Badge status={row.verificationStatus} />
        {row.changeRequest?.status === 'pending' && <Badge status="pending" dot={false}>Change request</Badge>}
      </div>
    ),
  },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: row => (
        <div className="row-actions">
          {row.verificationStatus === 'pending' && (
            <>
              <Button size="sm" onClick={e => { e.stopPropagation(); setDecision({ company: row, status: 'approved' }); }}>
                Approve
              </Button>
              <Button size="sm" variant="danger" onClick={e => { e.stopPropagation(); setDecision({ company: row, status: 'rejected' }); }}>
                Reject
              </Button>
            </>
          )}
          <Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); openDetail(row); }}>View</Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHead
        title="Companies"
        subtitle="Verify KYC, manage credit limits and control who can trade on the network."
      />

      <Tabs
        tabs={TABS}
        value={list.filters.verificationStatus}
        onChange={v => list.setFilter('verificationStatus', v)}
      />

      <div className="filters">
        <SearchBox
          value={list.filters.search}
          onChange={v => list.setFilter('search', v)}
          placeholder="Search by name, Company ID or GSTIN…"
        />
        <div style={{ minWidth: 190 }}>
          <select className="select" value={list.filters.type} onChange={e => list.setFilter('type', e.target.value)}>
            <option value="">All company types</option>
            {TYPES.map(t => (
              <option key={t} value={t}>{t[0].toUpperCase() + t.slice(1)}</option>
            ))}
          </select>
        </div>
        <div className="grow" />
        <span className="text-muted text-sm">{list.meta ? `${num(list.meta.total)} companies` : ''}</span>
      </div>

      <DataTable
        columns={columns}
        rows={list.rows}
        loading={list.loading}
        meta={list.meta}
        onPage={list.setPage}
        onRowClick={openDetail}
      />

      {/* ------------------------------------------------------- detail */}
      <Modal
        open={!!detail}
        size="wide"
        title={detail?.name}
        subtitle={detail ? `${detail.code} · ${detail.type}` : ''}
        onClose={() => setDetail(null)}
        footer={
          detail && (
            <>
              {detail.changeRequest?.status === 'pending' && (
                <Button onClick={() => { setAppeal(detail); setAdminNote(''); }}>
                  Review change request
                </Button>
              )}
              <Button variant="ghost" onClick={() => { setCredit(detail); setCreditValue(String(detail.creditLimit || 0)); }}>
                Set credit limit
              </Button>
              {detail.verificationStatus !== 'approved' && (
                <Button onClick={() => setDecision({ company: detail, status: 'approved' })}>Approve KYC</Button>
              )}
              {detail.verificationStatus !== 'rejected' && (
                <Button variant="danger" onClick={() => setDecision({ company: detail, status: 'rejected' })}>Reject</Button>
              )}
            </>
          )
        }
      >
        {detail && (
          <>
            <div className="row mb-2" style={{ gap: 10 }}>
              <Badge status={detail.verificationStatus} />
              {detail.gstVerified && <Badge status="verified" tone="brand">GST verified</Badge>}
              {detail.isActive ? <Badge status="active" /> : <Badge status="blocked">Inactive</Badge>}
            </div>

            <KV
              items={[
                { label: 'Company ID', value: <span className="mono-chip">{detail.code}</span> },
                { label: 'GSTIN', value: detail.gstNumber || '—' },
                detail.gstNumber && { label: 'GST status', value: detail.gstActive ? 'Active' : 'Inactive' },
                detail.gstLegalName && { label: 'GST legal name', value: detail.gstLegalName },
                { label: 'Details locked', value: detail.detailsLocked ? 'Yes' : 'No — editable' },
                detail.editsRemaining > 0 && { label: 'Edits granted', value: String(detail.editsRemaining) },
                { label: 'PAN', value: detail.panNumber || '—' },
                { label: 'Owner', value: detail.owner ? `${detail.owner.name} · ${detail.owner.mobile}` : '—' },
                { label: 'Email', value: detail.email || '—' },
                { label: 'Mobile', value: detail.mobile || '—' },
                { label: 'Registered', value: dateStr(detail.createdAt) },
                { label: 'Lifetime business', value: inr(detail.totalBusiness) },
                { label: 'Total orders', value: num(detail.totalOrders) },
                { label: 'Credit limit', value: `${inr(detail.creditLimit)} (used ${inr(detail.creditUsed)})` },
                detail.rejectionReason && { label: 'Rejection reason', value: detail.rejectionReason },
              ]}
            />

            {(detail.addresses || []).length > 0 && (
              <>
                <div className="divider" />
                <div className="card-title mb-2">Addresses</div>
                {detail.addresses.map(a => (
                  <div key={a._id} className="text-sm" style={{ marginBottom: 8 }}>
                    <strong>{a.label}</strong> — {[a.line1, a.line2, a.city, a.state, a.pincode].filter(Boolean).join(', ')}
                  </div>
                ))}
              </>
            )}

            {(detail.documents || []).length > 0 && (
              <>
                <div className="divider" />
                <div className="card-title mb-2">KYC documents</div>
                <div className="row wrap">
                  {detail.documents.map(doc => (
                    <a key={doc._id} href={doc.url} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">
                      📎 {doc.type} — {doc.name || 'document'}
                    </a>
                  ))}
                </div>
              </>
            )}

            {(detail.manufacturingUnits || []).length > 0 && (
              <>
                <div className="divider" />
                <div className="card-title mb-2">Manufacturing units</div>
                {detail.manufacturingUnits.map(u => (
                  <div key={u._id || u.name} className="text-sm">
                    <strong>{u.name}</strong> — {u.city}, {u.state} · capacity {num(u.capacityPerMonth)}/month
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </Modal>

      {/* ------------------------------------------------------ decision */}
      <Modal
        open={!!decision}
        title={decision?.status === 'approved' ? 'Approve this company?' : 'Reject this application'}
        onClose={() => { setDecision(null); setReason(''); }}
        footer={
          <>
            <Button variant="ghost" onClick={() => { setDecision(null); setReason(''); }}>Cancel</Button>
            <Button variant={decision?.status === 'approved' ? 'primary' : 'danger'} loading={busy} onClick={submitDecision}>
              {decision?.status === 'approved' ? 'Approve & notify' : 'Reject & notify'}
            </Button>
          </>
        }
      >
        <p className="text-muted mb-2">
          {decision?.status === 'approved'
            ? `${decision?.company.name} will be able to list products, place orders and receive their Company ID. The owner is notified over push, WhatsApp and email.`
            : 'The owner will be notified with the reason below and can re-submit after fixing it.'}
        </p>
        <Textarea
          label={decision?.status === 'approved' ? 'Internal note (optional)' : 'Reason for rejection'}
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder={decision?.status === 'approved' ? 'Documents checked against GST portal' : 'GST certificate is illegible — please re-upload'}
        />
      </Modal>

      {/* -------------------------------------------------- change request */}
      <Modal
        open={!!appeal}
        title="Review change request"
        subtitle={appeal?.name}
        onClose={() => setAppeal(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setAppeal(null)}>Cancel</Button>
            <Button variant="danger" loading={busy} onClick={() => decideAppeal('rejected')}>Decline</Button>
            <Button loading={busy} onClick={() => decideAppeal('approved')}>Approve one edit</Button>
          </>
        }
      >
        <p className="text-muted mb-2">
          Approving unlocks this company's name, GSTIN and PAN for <strong>exactly one</strong> save. The
          record locks again immediately afterwards.
        </p>

        {appeal?.changeRequest?.reason && (
          <div className="card" style={{ padding: 14, marginBottom: 16, background: 'var(--surface-2)' }}>
            <div className="label">Their reason</div>
            <div className="text-sm mt-1">{appeal.changeRequest.reason}</div>
            <div className="text-muted text-sm mt-1">Requested {dateStr(appeal.changeRequest.requestedAt)}</div>
          </div>
        )}

        <KV
          items={[
            { label: 'Current name', value: appeal?.name },
            { label: 'Current GSTIN', value: appeal?.gstNumber || '—' },
            { label: 'Current PAN', value: appeal?.panNumber || '—' },
          ]}
        />

        <Textarea
          label="Note to the applicant"
          value={adminNote}
          onChange={e => setAdminNote(e.target.value)}
          placeholder="Checked against the updated GST certificate."
        />
      </Modal>

      {/* -------------------------------------------------------- credit */}
      <Modal
        open={!!credit}
        title="Set credit limit"
        subtitle={credit?.name}
        onClose={() => setCredit(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setCredit(null)}>Cancel</Button>
            <Button loading={busy} onClick={saveCredit}>Save limit</Button>
          </>
        }
      >
        <Input
          label="Credit limit (₹)"
          type="number"
          min={0}
          value={creditValue}
          onChange={e => setCreditValue(e.target.value)}
          hint="The buyer can place credit orders up to this amount. They are notified of the change."
        />
      </Modal>
    </>
  );
};

export default Companies;
